import './assets/app.css';
import { createApp, markRaw } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedState from 'pinia-plugin-persistedstate';
import * as Sentry from "@sentry/vue";
import App from './App.vue';
import router from './router';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { appQueryClient } from '@/services/appQueryClient';
import { installSearchDocumentAnnouncements } from '@/services/searchDocumentAnnouncements';
import { installReloadOnStaleBuild, navigationTargetOf } from '@/services/reloadOnStaleBuild';
import { groupRequestErrorsByRequest } from '@/services/sentryRequestErrorGrouping';

const pinia = createPinia();
pinia.use(piniaPluginPersistedState);

pinia.use(({ store }) => {
  store.$router = markRaw(router);
});

const app = createApp(App);
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
  environment: import.meta.env.MODE,
  sendDefaultPii: true,
  normalizeDepth: 10,
  tracePropagationTargets: [ /tabasco\.sauceindustries\.com/, /tabasco-api\.sauceindustries\.com/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend: groupRequestErrorsByRequest,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      // Bug reports go through Sentry.sendFeedback (source 'api'), and the
      // SDK only uploads the buffered pre-submission replay for that source
      // when this flag is on — without it the feedback event carries a
      // replay_id whose segment is never flushed, so reports showed almost
      // nothing from before the user hit submit (SAU-287). Buffer mode
      // retains the last ~60s (SDK constant); raising
      // replaysSessionSampleRate is the only way to capture more.
      _experiments: { autoFlushOnFeedback: true },
    }),
  ],
});
pinia.use(Sentry.createSentryPiniaPlugin());

app
  .use(pinia)
  .use(router)
  // The client is built in its own module and handed in, so that code outside a
  // component — `authStore.logout` — can empty it. Let the plugin create one and
  // it keeps it to itself, which is how a signed-out user's cached data survived
  // into the next person's session.
  .use(VueQueryPlugin, { queryClient: appQueryClient })
  .mount('#app');

// Records on screen are fetched again when the backend announces a write to them (SAU-530).
const uninstallSearchDocumentAnnouncements = installSearchDocumentAnnouncements(appQueryClient);
import.meta.hot?.dispose(uninstallSearchDocumentAnnouncements);

// A tab left open across a deploy loads the new build's files.
installReloadOnStaleBuild({ navigatingTo: navigationTargetOf(router) });

// Prevent scroll gestures from triggering browser navigation
// This is particularly important for macOS trackpad gestures
function preventScrollNavigation() {

  // Handle wheel events (trackpad scrolling)
  document.addEventListener('wheel', (event) => {
    const target = event.target as HTMLElement;
    
    // Find the closest scrollable parent
    let scrollableParent = target;
    while (scrollableParent && scrollableParent !== document.body) {
      const computedStyle = window.getComputedStyle(scrollableParent);
      const overflowX = computedStyle.overflowX;
      const overflowY = computedStyle.overflowY;
      
      if (overflowX === 'scroll' || overflowX === 'auto' || 
          overflowY === 'scroll' || overflowY === 'auto') {
        break;
      }
      scrollableParent = scrollableParent.parentElement as HTMLElement;
    }

    // If we're scrolling horizontally and at the boundary, prevent navigation
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      const element = scrollableParent || document.documentElement;
      const atLeftBoundary = element.scrollLeft <= 0 && event.deltaX < 0;
      const atRightBoundary = element.scrollLeft + element.clientWidth >= element.scrollWidth && event.deltaX > 0;
      
      if (atLeftBoundary || atRightBoundary) {
        event.preventDefault();
        return;
      }
    }

    // For vertical scrolling at document level, prevent overscroll navigation
    if (scrollableParent === document.body || !scrollableParent) {
      const atTopBoundary = window.scrollY <= 0 && event.deltaY < 0;
      const atBottomBoundary = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight && event.deltaY > 0;
      
      if (atTopBoundary || atBottomBoundary) {
        // Allow some tolerance for momentum scrolling
        if (Math.abs(event.deltaY) > 10) {
          event.preventDefault();
        }
      }
    }
  }, { passive: false });

  // Handle touch events for mobile devices
  let touchStartX = 0;
  let touchStartY = 0;

  document.addEventListener('touchstart', (event) => {
    touchStartX = event.touches[0].clientX;
    touchStartY = event.touches[0].clientY;
  }, { passive: true });

  document.addEventListener('touchmove', (event) => {
    if (event.touches.length !== 1) return;

    const touchX = event.touches[0].clientX;
    const touchY = event.touches[0].clientY;
    const deltaX = touchX - touchStartX;
    const deltaY = touchY - touchStartY;

    // If it's primarily a horizontal swipe
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      const target = event.target as HTMLElement;
      let scrollableParent = target;
      
      while (scrollableParent && scrollableParent !== document.body) {
        const computedStyle = window.getComputedStyle(scrollableParent);
        if (computedStyle.overflowX === 'scroll' || computedStyle.overflowX === 'auto') {
          break;
        }
        scrollableParent = scrollableParent.parentElement as HTMLElement;
      }

      if (!scrollableParent || scrollableParent === document.body) {
        // Prevent navigation gestures when not in a scrollable container
        event.preventDefault();
      }
    }
  }, { passive: false });
}

// Initialize scroll navigation prevention
preventScrollNavigation();
